import { useAtomValue } from 'jotai';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Separator } from '@rocco/components/separator';
import { formatNumberWithCommasAndDecimals } from '@rocco/utils/format-number';
import {
    getAutoFilterMessage,
    getSearchCategoryLabelFromIndex,
} from '@modules/search/util';
import { searchPageDataAtom } from '../states/search-page-data-atom';

/**
 * Renders the header above the search results grid.
 * Displays the total result count and the search query/category.
 * Includes a separator for desktop view.
 */
export const SearchPageGridHeader = () => {
    const { mobile: isMobile } = useAppDisplayModeContext();
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const searchQuery = pageDataFromAtom?.searchQuery ?? '';

    if (!searchQuery || !pageDataFromAtom) {
        // Return null if no search query or if essential data isn't loaded yet
        return null;
    }

    const totalCount = pageDataFromAtom.totalCount;
    const searchIndex = pageDataFromAtom.searchIndex;
    const filterStates = pageDataFromAtom.filterStates;
    const autoSearchFilters = pageDataFromAtom.autoSearchFilters;
    const categoryName =
        getSearchCategoryLabelFromIndex(searchIndex)?.toLowerCase();
    const autoFilterMessage = getAutoFilterMessage({
        autoSearchFilters,
        searchIndex,
        filterStates,
        totalCount,
    });

    return (
        <>
            {!isMobile && <Separator />}
            <div className="px-6 pt-4 pb-1 md:px-0 md:pt-5 md:pb-3">
                {autoFilterMessage ? (
                    <>
                        <div className="mb-2 text-lg font-medium tracking-[0.025rem] md:mb-4">
                            {autoFilterMessage.message}
                        </div>
                        <div className="text-base italic md:text-lg">
                            {autoFilterMessage.suggestion}
                        </div>
                    </>
                ) : (
                    <span className="text-lg font-medium tracking-[0.025rem]">
                        {`Showing ${formatNumberWithCommasAndDecimals(totalCount)} results for `}
                        <strong>{searchQuery}</strong>
                        {!!categoryName && (
                            <>
                                {' in '}
                                <strong>{categoryName}</strong>
                            </>
                        )}
                    </span>
                )}
            </div>
        </>
    );
};
